// TGCLOUD-1818 — the line under the grid that says what you are looking at.
//
// This is most of the value of making criteria visible, so it is not polish.
// The failure it removes is "No results" with nothing on screen to explain why:
// the empty state NAMES the cause ("No matching people. 2 filters active.") and
// puts Clear all next to it.
//
// aria-live="polite" because a screen-reader user gets no other signal that a
// chip did anything — the grid simply has fewer rows, silently.

import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

/**
 * @param {object} props
 * @param {number} props.shownCount     rows after chips + free text
 * @param {number} props.totalCount     rows before any narrowing
 * @param {number} props.familyCount    distinct families in the shown rows
 * @param {number} props.criteriaCount
 * @param {boolean} props.hasSearchText
 * @param {boolean} props.narrowed      any chip or free text active
 * @param {boolean} [props.pending]     criteria active but not yet applicable
 * @param {boolean} [props.unavailable] the field catalogue could not be fetched
 * @param {() => void} props.onClearAll
 * @param {(s: string) => string} props.t
 */
export default function FamilySearchResultLine({
  shownCount,
  totalCount,
  familyCount,
  criteriaCount,
  hasSearchText,
  narrowed,
  pending,
  unavailable,
  onClearAll,
  t
}) {
  // `pending` and `empty` look identical from the row count alone — both are
  // zero rows with filters active. Saying "No matching people" during the
  // former is a false negative in words, which on a patient search is the
  // reading that makes someone stop looking.
  const empty = shownCount === 0 && !pending && !unavailable;

  // "No results" alone is the thing this screen is trying to stop saying.
  const causeParts = [];
  if (criteriaCount > 0) {
    causeParts.push(
      `${criteriaCount} ${
        criteriaCount === 1 ? t('filter active') : t('filters active')
      }`
    );
  }
  if (hasSearchText) causeParts.push(t('a search term'));

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
        px: 3,
        py: 1.25
      }}
    >
      <Typography
        variant="body2"
        aria-live="polite"
        data-testid="family-search-result-line"
        sx={{ color: 'text.primary' }}
      >
        {unavailable ? (
          t('Filters could not be applied — the field list is unavailable.')
        ) : pending ? (
          t('Applying filters…')
        ) : empty && narrowed ? (
          <>
            {t('No matching people.')}
            {causeParts.length ? ` ${causeParts.join(', ')}.` : ''}
          </>
        ) : (
          <>
            {t('Showing')} <strong>{shownCount}</strong>{' '}
            {shownCount === 1 ? t('person') : t('people')}
            {familyCount > 0 && (
              <>
                {' '}
                {t('across')} <strong>{familyCount}</strong>{' '}
                {familyCount === 1 ? t('family') : t('families')}
              </>
            )}
            {narrowed && totalCount > shownCount && (
              <Typography
                component="span"
                variant="body2"
                // text.secondary, not text.disabled: this still has to clear
                // 4.5:1 against the surface.
                sx={{ color: 'text.secondary', ml: 1 }}
              >
                {t('filtered from')} {totalCount.toLocaleString()}
              </Typography>
            )}
          </>
        )}
      </Typography>

      {narrowed && (
        <Button
          size="small"
          onClick={onClearAll}
          sx={{ textTransform: 'none' }}
        >
          {t('Clear all')}
        </Button>
      )}
    </Box>
  );
}
